/**
 * Income Allocations Calculator
 *
 * For LLC/partnership investors where value is tracked via tax basis:
 * Tax Basis = Contributions + Income Allocations - Distributions + Net Transfers
 *
 * This calculator is used for entities like L&L Land North where:
 * - Contributions: Initial capital and additional capital calls
 * - Income Allocations: K-1 allocated income that increases tax basis
 * - Distributions: Cash distributed that reduces tax basis
 * - Transfers: Ownership transfers between investors (adjusts basis)
 *
 * Also calculates Market Value from valuations (shares × price) when available.
 * Output displays Tax Basis, Market Value, and Unrealized Gain/Loss.
 */

import { basisForBreakdown } from '../helpers/equity-calculation-utils.js';
import { attachCAGR } from '../helpers/cagr-utils.js';

const IncomeAllocationsCalculator = {
	methodId: 'income_allocations',
	displayName: 'Income Allocations (Tax Basis)',
	requiredDataSources: ['contributions', 'incomeAllocations', 'distributions'],

	// Graph configuration - time series line shows tax basis over time
	graphConfig: {
		recommendedType: 'time_series_line',
		timeSeriesStrategy: 'income_allocations',
		supportsTimeSeries: true,
	},

	// Received transfers inflate taxBasis but aren't performance, so exclude
	// them from CAGR value — same rationale as ContributionsAccruals.
	cagrOptions: { excludeTransfers: true },

	/**
	 * Calculate tax basis from contributions, income allocations, distributions, and transfers
	 * Also calculates market value from valuations when available
	 * @param {Object} input
	 * @param {Object} input.holdings - { contributions, accruals, distributions, transfers, owned, sharePrice } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings || {};
		const shareClasses = input.shareClasses || Object.keys(holdings.contributions || {});
		const useFundLanguage = input.options?.useFundLanguage || false;

		// Ensure all required objects exist with fallback to empty objects
		const contributionsData = holdings.contributions || {};
		const incomeAllocationsData = holdings.accruals || {};
		const distributionsData = holdings.distributions || {};
		const transfersData = holdings.transfers || {};
		const ownedData = holdings.owned || {};
		const sharePriceData = holdings.sharePrice || {};
		const totalClassOwnedData = holdings.totalClassOwned || {};
		const denominationData = holdings.denomination || {};

		let totalContributions = 0;
		let totalIncomeAllocations = 0;
		let totalDistributions = 0;
		let totalTransfers = 0;
		let totalMarketValue = 0;
		const breakdown = [];

		shareClasses.forEach(function (key) {
			const contributions = contributionsData[key] || 0;
			const incomeAllocations = incomeAllocationsData[key] || 0;
			const distributions = distributionsData[key] || 0;
			const netTransfers = transfersData[key] || 0;
			const owned = ownedData[key] || 0;
			const sharePrice = sharePriceData[key] || 0;

			// Formula: Tax Basis = Contributions + Income Allocations - Distributions + Net Transfers
			const taxBasis = contributions + incomeAllocations - distributions + netTransfers;

			// Market Value depends on how "owned" is expressed (resolved via denomination-resolver):
			// - 'shares': owned is a share count → owned × sharePrice
			// - 'dollars' with totalClassOwned: owned is capital account → (owned / totalClassOwned) × fund NAV
			// - 'dollars' without totalClassOwned: owned IS the capital balance / market value
			// - 'percent': owned is a percentage → (owned / 100) × entity value
			const totalClassOwned = totalClassOwnedData[key] || 0;
			const denomination = denominationData[key] || (useFundLanguage ? 'dollars' : 'percent');
			const marketValue =
				denomination === 'shares'
					? owned * sharePrice
					: denomination === 'dollars' && totalClassOwned > 0 && sharePrice > 0
						? (owned / totalClassOwned) * sharePrice
						: denomination === 'dollars'
							? owned
							: (owned / 100) * sharePrice;

			// Unrealized Gain/Loss = Market Value - Tax Basis
			const unrealizedGain = marketValue - taxBasis;

			totalContributions += contributions;
			totalIncomeAllocations += incomeAllocations;
			totalDistributions += distributions;
			totalTransfers += netTransfers;
			totalMarketValue += marketValue;

			// value = marketValue when a price is available, otherwise taxBasis
			const classValue = sharePrice > 0 && marketValue > 0 ? marketValue : taxBasis;
			breakdown.push({
				classKey: key,
				className: key,
				value: classValue,
				taxBasis: taxBasis,
				contributions: contributions,
				incomeAllocations: incomeAllocations,
				distributions: distributions,
				transfers: netTransfers,
				owned: owned,
				sharePrice: sharePrice,
				marketValue: marketValue,
				currentValue: marketValue, // Alias for headline templates
				unrealizedGain: unrealizedGain,
				basis: basisForBreakdown(holdings, key),
			});
		});

		const totalTaxBasis = totalContributions + totalIncomeAllocations - totalDistributions + totalTransfers;
		const totalUnrealizedGain = totalMarketValue - totalTaxBasis;
		const totalPaidInCapital = totalContributions;
		const netValue = totalTaxBasis - totalPaidInCapital;

		// Use market value as the primary display value when it's ownership-weighted (totalClassOwned configured)
		// Falls back to tax basis when no fund NAV valuation is available
		const hasOwnershipWeightedMarketValue = shareClasses.some((k) => (totalClassOwnedData[k] || 0) > 0 && (sharePriceData[k] || 0) > 0);
		const totalValue = hasOwnershipWeightedMarketValue && totalMarketValue > 0 ? totalMarketValue : totalTaxBasis;

		const result = {
			totalValue: totalValue,
			totalTaxBasis: totalTaxBasis,
			totalContributions: totalContributions,
			totalIncomeAllocations: totalIncomeAllocations,
			totalDistributions: totalDistributions,
			totalTransfers: totalTransfers,
			totalMarketValue: totalMarketValue,
			totalCurrentValue: totalMarketValue,
			totalUnrealizedGain: totalUnrealizedGain,
			totalPaidInCapital: totalPaidInCapital,
			netValue: netValue,
			breakdown: breakdown,
			fundMetrics: null,
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
			},
		};

		attachCAGR(result, holdings.cagrAnchors, { ...this.cagrOptions, ...holdings.cagrOptions });
		return result;
	},
};

export { IncomeAllocationsCalculator };
